/**
 * Pagination scroll handler
 * Scrolls to the top of the section after page change via Section Rendering API
 */

const PAGINATION_SELECTOR = '.c-pagination';
const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Extracts section_id from a URL
 * @param {string} urlString - The URL string
 * @returns {string|null} The section ID or null if not found
 */
function extractSectionId(urlString) {
    try {
        const url = new URL(urlString);
        const sectionId = url.searchParams.get('section_id');
        return sectionId || null;
    } catch (error) {
        if (Shopify.designMode) {
            console.warn('Pagination: Failed to extract section_id from URL', {
                url: urlString,
                error: error instanceof Error ? error.message : String(error)
            });
        }
        return null;
    }
}

/**
 * Gets URL string from fetch arguments
 * @param {any} url - The URL argument
 * @returns {string} The URL string
 */
function getUrlString(url) {
    if (typeof url === 'string') return url;
    if (url instanceof URL) return url.toString();
    if (url instanceof Request) return url.url;
    return String(url || '');
}

/**
 * Shows loading state
 * @param {string} sectionId - The section ID
 * @returns {boolean} True if loader was shown successfully, false otherwise
 */
function showLoader(sectionId) {
    try {
        const normalizedId = sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
        const sectionSelector = `#${SECTION_ID_PREFIX}${normalizedId}`;
        const sectionElement = document.querySelector(sectionSelector);

        if (!(sectionElement instanceof HTMLElement)) {
            if (Shopify.designMode) {
                console.warn('Pagination: Section element not found', {
                    sectionId,
                    selector: sectionSelector
                });
            }
            return false;
        }

        // Ensure section has position relative for absolute positioning of overlay
        try {
            const computedStyle = window.getComputedStyle(sectionElement);
            if (computedStyle.position === 'static') {
                sectionElement.style.position = 'relative';
            }
        } catch (error) {
            if (Shopify.designMode) {
                console.warn('Pagination: Failed to set section position', {
                    sectionId,
                    error: error instanceof Error ? error.message : String(error)
                });
            }
        }

        // Find overlay and move it to section if needed
        const overlay = document.querySelector('.c-pagination-loader');
        if (overlay instanceof HTMLElement) {
            try {
                // Move overlay to section level if it's not already there
                if (overlay.parentElement !== sectionElement) {
                    sectionElement.appendChild(overlay);
                }
                overlay.removeAttribute('hidden');
            } catch (error) {
                if (Shopify.designMode) {
                    console.warn('Pagination: Failed to show overlay', {
                        sectionId,
                        error: error instanceof Error ? error.message : String(error)
                    });
                }
            }
        }

        // Show spinner (it's fixed, so location doesn't matter)
        const spinner = document.querySelector('.c-pagination-loader__spinner');
        if (spinner instanceof HTMLElement) {
            try {
                spinner.removeAttribute('hidden');
            } catch (error) {
                if (Shopify.designMode) {
                    console.warn('Pagination: Failed to show spinner', {
                        sectionId,
                        error: error instanceof Error ? error.message : String(error)
                    });
                }
            }
        }

        return true;
    } catch (error) {
        if (Shopify.designMode) {
            console.error('Pagination: Failed to show loader', {
                sectionId,
                error: error instanceof Error ? error.message : String(error)
            });
        }
        return false;
    }
}

/**
 * Hides loading state
 * @param {string} sectionId - The section ID
 * @param {Error} [error] - Optional error that occurred
 */
function hideLoader(sectionId, error) {
    try {
        // Hide overlay
        const overlay = document.querySelector('.c-pagination-loader');
        if (overlay instanceof HTMLElement) {
            overlay.setAttribute('hidden', '');
        }

        // Hide spinner
        const spinner = document.querySelector('.c-pagination-loader__spinner');
        if (spinner instanceof HTMLElement) {
            spinner.setAttribute('hidden', '');
        }

        // Log error if provided
        if (error && Shopify.designMode) {
            console.error('Pagination: Error during pagination', {
                sectionId,
                error: error instanceof Error ? error.message : String(error),
                stack: error instanceof Error ? error.stack : undefined
            });
        }
    } catch (hideError) {
        if (Shopify.designMode) {
            console.error('Pagination: Failed to hide loader', {
                sectionId,
                originalError: error instanceof Error ? error.message : String(error),
                hideError: hideError instanceof Error ? hideError.message : String(hideError)
            });
        }
    }
}

/**
 * Scrolls to the top of a section
 * @param {string} sectionId - The section ID
 * @returns {boolean} True if scroll was successful, false otherwise
 */
function scrollToSection(sectionId) {
    try {
        const normalizedId = sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
        const sectionSelector = `#${SECTION_ID_PREFIX}${normalizedId}`;
        const sectionElement = document.querySelector(sectionSelector);

        if (!(sectionElement instanceof HTMLElement)) {
            if (Shopify.designMode) {
                console.warn('Pagination: Section element not found for scrolling', {
                    sectionId,
                    selector: sectionSelector
                });
            }
            return false;
        }

        try {
            const sectionTop = sectionElement.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({
                top: sectionTop,
                behavior: 'smooth',
            });
            return true;
        } catch (scrollError) {
            if (Shopify.designMode) {
                console.warn('Pagination: Failed to scroll to section', {
                    sectionId,
                    error: scrollError instanceof Error ? scrollError.message : String(scrollError)
                });
            }
            return false;
        }
    } catch (error) {
        if (Shopify.designMode) {
            console.error('Pagination: Error in scrollToSection', {
                sectionId,
                error: error instanceof Error ? error.message : String(error)
            });
        }
        return false;
    }
}

/**
 * Initializes pagination scroll handler
 */
function initPaginationScroll() {
    // Check if pagination exists
    if (!document.querySelector(PAGINATION_SELECTOR)) return;

    // Store original fetch
    const originalFetch = window.fetch;

    // Intercept fetch requests
    window.fetch = function (...args) {
        const urlString = getUrlString(args[0]);

        // Check if it's a Section Rendering API request for pagination
        // Only intercept if it's a pagination request (has page parameter or comes from pagination)
        if (urlString.includes('section_id=')) {
            try {
                const url = new URL(urlString, window.location.origin);
                const hasPageParam = url.searchParams.has('page');
                
                // Only intercept pagination requests (those with page parameter)
                // Filter requests don't have page parameter, so they won't be intercepted
                if (hasPageParam) {
                    const sectionId = extractSectionId(urlString);

                    if (!sectionId) {
                        if (Shopify.designMode) {
                            console.warn('Pagination: No section_id found in pagination request', {
                                url: urlString
                            });
                        }
                        // Continue with original fetch if no section_id
                        return originalFetch.apply(this, args);
                    }

                    // Show loader immediately
                    const loaderShown = showLoader(sectionId);
                    
                    // Set a timeout to ensure loader is hidden even if something goes wrong
                    const loaderTimeout = setTimeout(() => {
                        hideLoader(sectionId, new Error('Loader timeout - forcing hide'));
                    }, 30000); // 30 seconds max

                    const fetchPromise = originalFetch.apply(this, args);

                    // After response is received and processed, hide loader and scroll
                    fetchPromise
                        .then((/** @type {Response} */ response) => {
                            clearTimeout(loaderTimeout);

                            if (!response.ok) {
                                const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
                                hideLoader(sectionId, error);
                                
                                if (Shopify.designMode) {
                                    console.error('Pagination: Request failed', {
                                        sectionId,
                                        status: response.status,
                                        statusText: response.statusText,
                                        url: urlString
                                    });
                                }
                                return;
                            }

                            // Wait for morphing to complete (next frame)
                            // Use multiple frames to ensure DOM is fully updated
                            requestAnimationFrame(() => {
                                requestAnimationFrame(() => {
                                    try {
                                        // Verify section still exists before scrolling
                                        const normalizedId = sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
                                        const sectionSelector = `#${SECTION_ID_PREFIX}${normalizedId}`;
                                        const sectionElement = document.querySelector(sectionSelector);

                                        if (!(sectionElement instanceof HTMLElement)) {
                                            const error = new Error(`Section ${sectionId} not found after morphing`);
                                            hideLoader(sectionId, error);
                                            return;
                                        }

                                        hideLoader(sectionId);
                                        scrollToSection(sectionId);
                                    } catch (morphError) {
                                        hideLoader(sectionId, morphError instanceof Error ? morphError : new Error(String(morphError)));
                                    }
                                });
                            });
                        })
                        .catch((/** @type {unknown} */ error) => {
                            clearTimeout(loaderTimeout);
                            
                            // Hide loader on error
                            hideLoader(sectionId, error instanceof Error ? error : new Error(String(error)));
                            
                            // Re-throw error to maintain original behavior
                            throw error;
                        });

                    return fetchPromise;
                }
            } catch (urlError) {
                // If URL parsing fails, continue with original fetch
                // This handles relative URLs or malformed URLs
                if (Shopify.designMode) {
                    console.warn('Pagination: URL parsing failed, using original fetch', {
                        url: urlString,
                        error: urlError instanceof Error ? urlError.message : String(urlError)
                    });
                }
            }
        }

        // For all other requests, use original fetch
        return originalFetch.apply(this, args);
    };
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPaginationScroll);
} else {
    initPaginationScroll();
}
